/**
 * サーバーが作る入力の見え方（`state.input`。Issue #663 / #665）を画面で使うための**形の道具**（判断は持たない）。
 * 問い合わせの読み方と答え方・日本語の文はサーバー（server/synchro_server/ocg/input_view.py）。ここは「押したもの → 問い合わせの答え」の
 * 対応付けと、ticker の増えた分の切り出しだけ。
 *
 * input の形（主なもの）: query { id, player, source: start|register|respond, kind, message, context, options[{ index, label, payload?, place?, key?, bit?, code?, ... }],
 *   min, max, value_kind?, view（応答タブがそのまま描く）, auto?（自動で「チェーンしない」で通してよい）, activatable? } /
 *   register { items, all, raw（一次データの形の項目。value_text・board_key 付き）, hold_key } / texts { last_event, waiting, phase, turn, ... } /
 *   undo_count / skipped_chances（label・rows 付き）/ ticker [{ id, text }] / head / status / life / last_ms / gray_keys / names / unconfirmed_since_last_answer
 */

/** 1 つを出しておく時間（人が読み取れるくらい） */
export const TICKER_MS = 300;

/** 鍵の順に依らない比較（iPad の JSON は鍵の順を保たない） */
const canon = (v) => (Array.isArray(v) ? v.map(canon)
  : v && typeof v === 'object' ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, canon(v[k])])) : v);
const same = (a, b) => JSON.stringify(canon(a)) === JSON.stringify(canon(b));

/**
 * 押したもの → 今の問い合わせへの答え（OcgAnswer の answer）。合う選択肢が無ければ null。
 *   { payload }: その payload を持つ選択肢（行動・フェイズ・パス・はい/いいえ・やめる・終える）
 *   { picks }: 選択肢の index（カードの選択）
 *   { places }: 置く場所（options[].place と同じもの）
 *   { mask }: ビットの和（options[].bit）
 *   { value }: 並べ替え・数え・カードの宣言
 */
export function queryAnswer(query, intent) {
  if (!query || !intent) return null;
  const opts = query.options ?? [];
  if (intent.payload !== undefined) {
    const o = opts.find((x) => x.payload !== undefined && same(x.payload, intent.payload));
    return o ? { id: query.id, picks: [o.index] } : null;
  }
  if (intent.picks) return { id: query.id, picks: intent.picks };
  if (intent.places) {
    const picks = intent.places.map((p) => opts.find((x) => x.place && same(x.place, p))?.index);
    return picks.every((i) => i != null) ? { id: query.id, picks } : null;
  }
  if (intent.mask != null) return { id: query.id, picks: opts.filter((o) => o.bit && intent.mask & o.bit).map((o) => o.index) };
  if (intent.value !== undefined) return { id: query.id, value: intent.value };
  return null;
}

/** ticker の増えた分（前の列と先頭から同じところまでを飛ばす。戻して入れ直した分も流れる） */
export function addedTicker(prev = [], next = []) {
  let i = 0;
  while (i < prev.length && i < next.length && prev[i].id === next[i].id && prev[i].text === next[i].text) i += 1;
  return next.slice(i);
}

/** デュエル開始時のイベント（最初の応答より前＝op_seq の無いもの。初手のドローなど）の ticker */
export function startTicker(input, events = []) {
  const ids = new Set((events ?? []).filter((e) => e.op_seq == null).map((e) => e.id ?? String(e.index)));
  return (input?.ticker ?? []).filter((t) => ids.has(t.id));
}


// 置く場所（options[].place = { player, location, sequence }）の鍵。盤面のカード・ゾーンの鍵と同じ形（stage.js の zoneKeyOfPlaceKey が読む）
const LOCATION_NAME = { 1: 'maindeck', 2: 'hand', 4: 'monster_field', 8: 'magic_trap', 16: 'grave', 32: 'removed', 64: 'extra' };
export const placeKey = (place) => `${place.player}:${LOCATION_NAME[place.location]}:${place.sequence}`;

/** 置く場所の枠を 1 つ押したときの次の選択。count 個そろったら { done: true, places } */
export function pickPlace(current, place, count = 1) {
  const eq = (p) => p.player === place.player && p.location === place.location && p.sequence === place.sequence;
  const next = current.some(eq) ? current.filter((p) => !eq(p)) : [...current, place];
  return next.length >= count ? { done: true, places: next.slice(0, count) } : { done: false, places: next };
}
